import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IFetchStatsAction,
    ISetNotificationsAction,
    IUpdateNotificationsReadAction,
    IFetchAssignmentsAction,
    IUpdateAssignmentAction,
    ICreateAssignmentAction,
    IDeleteAssignmentAction,
    ISetTrackableStatisticsAction,
} from './interface';
import {
    IClientNotification,
    IStatsCard,
    IClientAssignment,
    IClientTrackableStatistics,
} from '../../../common';

interface IDashboardSlice {
    stats: Record<string, Array<IStatsCard>>;
    lastUpdated: number | null;
    notifications: Array<IClientNotification>;
    assignments: Array<IClientAssignment>;
    completedAssignments: Array<IClientAssignment>;
    trackableStatistics: IClientTrackableStatistics;
    uiState: {
        isLoadingStats: boolean;
        isLoadingNotifications: boolean;
        isUpdatingNotifications: boolean;
        isLoadingAssignments: boolean;
        isUpdatingAssignment: boolean;
        isCreatingAssignment: boolean;
        isDeletingAssignment: boolean;
        isLoadingTrackableStatistics: boolean;
    };
}

const initialState: IDashboardSlice = {
    stats: {},
    lastUpdated: null,
    notifications: [],
    assignments: [],
    completedAssignments: [],
    trackableStatistics: {
        week: {
            public: {},
            not_public: {},
        },
        month: {
            public: {},
            not_public: {},
        },
        quarter: {
            public: {},
            not_public: {},
        },
        year: {
            public: {},
            not_public: {},
        },
    },
    uiState: {
        isLoadingStats: false,
        isLoadingNotifications: false,
        isUpdatingNotifications: false,
        isLoadingAssignments: false,
        isUpdatingAssignment: false,
        isCreatingAssignment: false,
        isDeletingAssignment: false,
        isLoadingTrackableStatistics: false,
    },
};

export const sliceName = 'dashboard';
export const dashboardSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        fetchStats: (state) => {
            state.uiState.isLoadingStats = true;
        },
        setStats: (state, action: PayloadAction<IFetchStatsAction>) => {
            state.stats = action.payload.stats;
            state.lastUpdated = Date.now();
            state.uiState.isLoadingStats = false;
        },
        fetchStatsDone: (state) => {
            state.uiState.isLoadingStats = false;
        },
        fetchNotifications: (state) => {
            state.uiState.isLoadingNotifications = true;
        },
        setNotifications: (
            state,
            action: PayloadAction<ISetNotificationsAction>
        ) => {
            state.notifications = action.payload.notifications;
            state.uiState.isLoadingNotifications = false;
        },
        fetchNotificationsDone: (state) => {
            state.uiState.isLoadingNotifications = false;
        },
        updateNotificationsRead: (
            state,
            _action: PayloadAction<IUpdateNotificationsReadAction>
        ) => {
            state.uiState.isUpdatingNotifications = true;
        },
        updateNotificationsReadDone: (state) => {
            state.uiState.isUpdatingNotifications = false;
        },
        fetchAssignments: (state) => {
            state.uiState.isLoadingAssignments = true;
        },
        setAssignments: (
            state,
            action: PayloadAction<IFetchAssignmentsAction>
        ) => {
            state.assignments = action.payload.assignments ?? [];
        },
        setCompletedAssignments: (
            state,
            action: PayloadAction<IFetchAssignmentsAction>
        ) => {
            state.completedAssignments =
                action.payload.completedAssignments ?? [];
        },
        fetchAssignmentsDone: (state) => {
            state.uiState.isLoadingAssignments = false;
        },
        updateAssignment: (
            state,
            _action: PayloadAction<IUpdateAssignmentAction>
        ) => {
            state.uiState.isUpdatingAssignment = true;
        },
        updateAssignmentDone: (state) => {
            state.uiState.isUpdatingAssignment = false;
        },
        createAssignment: (
            state,
            _action: PayloadAction<ICreateAssignmentAction>
        ) => {
            state.uiState.isCreatingAssignment = true;
        },
        createAssignmentDone: (state) => {
            state.uiState.isCreatingAssignment = false;
        },
        deleteAssignment: (
            state,
            _action: PayloadAction<IDeleteAssignmentAction>
        ) => {
            state.uiState.isDeletingAssignment = true;
        },
        deleteAssignmentDone: (state) => {
            state.uiState.isDeletingAssignment = false;
        },
        fetchTrackableStatistics: (state) => {
            state.uiState.isLoadingTrackableStatistics = true;
        },
        setTrackableStatistics: (
            state,
            action: PayloadAction<ISetTrackableStatisticsAction>
        ) => {
            state.trackableStatistics = action.payload.trackableStatistics;
        },
        fetchTrackableStatisticsDone: (state) => {
            state.uiState.isLoadingTrackableStatistics = false;
        },
    },
});

export const dashboardActions = dashboardSlice.actions;
export default dashboardSlice.reducer;
